<template>

<header class="topbar">

	<app-wordmark :size="22" :mark="32" tagline />

	<nav class="topbar-nav" aria-label="Primary">
		<router-link to="/" class="topbar-link" exact-active-class="is-current">Today</router-link>
		<router-link to="/archive" class="topbar-link" active-class="is-current">Archive</router-link>
		<router-link to="/leaderboard" class="topbar-link" active-class="is-current">Leaderboard</router-link>
		<router-link v-if="isAdmin" to="/curate" class="topbar-link" active-class="is-current">Curate</router-link>
	</nav>

	<div class="topbar-right">

		<div v-if="signedIn" class="topbar-account-wrap">

			<button
				type="button"
				class="topbar-account"
				aria-label="Account"
				aria-haspopup="true"
				:aria-expanded="menuOpen ? 'true' : 'false'"
				v-on:click="toggleMenu"
			>
				<span class="topbar-avatar" :style="{ backgroundColor: avatarBg }">
					<img
						v-if="avatar && !avatarFailed"
						class="topbar-avatar-img"
						:src="avatar"
						alt=""
						referrerpolicy="no-referrer"
						v-on:error="avatarFailed = true"
					>
					<span v-else class="topbar-initial">{{ initial }}</span>
				</span>
				<span class="topbar-name">{{ name }}</span>
			</button>

			<div v-if="menuOpen" class="topbar-menu" role="menu">
				<span class="topbar-menu-name">{{ name }}</span>
				<router-link to="/dashboard" class="topbar-menu-item" role="menuitem" v-on:click.native="menuOpen = false">Dashboard</router-link>
				<router-link to="/settings" class="topbar-menu-item" role="menuitem" v-on:click.native="menuOpen = false">Settings</router-link>
				<button v-if="deviceSyncCount > 0" type="button" class="topbar-menu-item" role="menuitem" v-on:click="syncPlays">Sync device plays</button>
				<button type="button" class="topbar-menu-item" role="menuitem" v-on:click="logout">Sign out</button>
			</div>

		</div>

		<button v-else type="button" class="topbar-signin" aria-label="Sign in" v-on:click="signin">
			<span class="topbar-signin-text">Sign in</span>
			<!-- Font Awesome "user" (solid) glyph, inlined — shown on mobile where
			     the button collapses to a 32x32 icon square -->
			<svg class="topbar-signin-icon" viewBox="0 0 448 512" aria-hidden="true"><path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512l388.6 0c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304l-91.4 0z"/></svg>
		</button>

		<!-- changelog: opens the modal (hosted in App via bus). Sized like the
		     sign-in button, placed after it. A recent unread entry shows a red dot
		     and shakes the chip every few seconds. -->
		<button type="button" class="topbar-version" :class="{ 'is-unread': changelogUnread }" aria-label="Changelog" v-on:click="openChangelog">
			<span class="topbar-version-label">v{{ version }}</span>
			<span v-if="changelogUnread" class="topbar-version-dot" aria-hidden="true"></span>
		</button>

	</div>

</header>

</template>

<script>

/*
	The one site header, rendered outside the router so it never moves between
	screens: the boxart wordmark left, Today / Archive / Leaderboard nav centre,
	and on the right either a Sign in prompt (guest) or the signed-in account's
	avatar + name. Sign in raises a global `signin` on the bus (App.vue hosts the
	one sign-in dialog) AND emits `signin` for any screen that wants it locally;
	the account chip opens a tiny dropdown with the name and Sign out (there is no
	/account screen).
*/

import bus from '../../service/bus'
import util from '../../service/util'
import { latest, unread, markSeen } from '../../service/changelog'

export default {

	data: function() {

		return {
			// whether the account dropdown menu is open
			menuOpen: false,
			// set if the avatar image fails to load (e.g. ORB-blocked Google pic),
			// so we fall back to the coloured initial
			avatarFailed: false,
			// count of local device plays available to sync (refreshed when the
			// menu opens); gates the "Sync device plays" item
			deviceSyncCount: 0,
			// whether there's an unread recent changelog entry (drives the dot +
			// shake on the version chip)
			changelogUnread: false
		}

	},

	watch: {

		// a new avatar (sign-in / account switch) gets a fresh chance to load
		avatar: function() {

			this.avatarFailed = false

		}

	},

	created: function() {

		// close the menu on any outside click
		var self = this

		this.onDocClick = function(e) {

			if (!self.menuOpen) return
			if (self.$el && self.$el.contains(e.target)) return
			self.menuOpen = false

		}

		document.addEventListener('click', this.onDocClick)

		// nudge about a recent changelog the user hasn't opened (red dot + shake)
		this.changelogUnread = unread()

	},

	beforeDestroy: function() {

		document.removeEventListener('click', this.onDocClick)

	},

	methods: {

		signin: function() {

			// the dialog is hosted once at the App root; the bus lets any topbar
			// (every screen renders its own) open it without prop-drilling
			bus.$emit('signin')
			this.$emit('signin')

		},

		// open the changelog modal (hosted once at the App root, via the bus), and
		// clear the unread nudge (remembering this version as seen)
		openChangelog: function() {

			bus.$emit('changelog')

			if (this.changelogUnread) {

				markSeen()
				this.changelogUnread = false

			}

		},

		toggleMenu: function() {

			this.menuOpen = !this.menuOpen

			// refresh the count of syncable device plays when opening, so the
			// "Sync device plays" item only shows when there's something to sync
			if (this.menuOpen) {

				var self = this

				Promise.resolve(this.$store.dispatch('game/deviceSyncable')).then(function(n) {

					self.deviceSyncCount = n || 0

				})

			}

		},

		// sync this device's local plays onto the account (live -> results,
		// practice -> practice). Opens the dialog immediately; it computes the gap
		// itself (with a loading state). Only surfaced when there are local plays.
		syncPlays: function() {

			this.menuOpen = false
			bus.$emit('sync-open', { force: true })

		},

		logout: function() {

			this.menuOpen = false
			this.$store.dispatch('account/logout')

			// back to Today; avoid a redundant navigation error if already there
			if (this.$route.path !== '/') this.$router.push('/').catch(function() {})

		}

	},

	computed: {

		account: function() {

			return this.$store.getters['account/account']

		},

		signedIn: function() {

			return this.$store.getters['account/signedIn']

		},

		// curators get a Curate link in the nav (after Leaderboard)
		isAdmin: function() {

			return this.$store.getters['account/isAdmin']

		},

		// the changelog chip label, from the newest build, so the button and the
		// modal always show the same version
		version: function() {

			return latest

		},

		name: function() {

			return (this.account && this.account.name) || 'Account'

		},

		avatar: function() {

			return (this.account && this.account.avatar) || null

		},

		// the colour behind the initial (only seen when there's no avatar image,
		// or it failed to load)
		avatarBg: function() {

			return util.avatarColor(this.initial)

		},

		initial: function() {

			return this.name ? this.name.charAt(0).toUpperCase() : '?'

		}

	}

}

</script>

<style scoped>

.topbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 18px;
	padding: 16px 20px;
	border-bottom: 1px solid var(--hairline);
}

.topbar-nav {
	display: flex;
	align-items: center;
	gap: 26px;
	margin-left: auto;
}

.topbar-link {
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted);
	text-decoration: none;
}

.topbar-link:hover {
	color: var(--ink);
}

.topbar-link.is-current {
	color: var(--ink);
	font-weight: 500;
}

.topbar-right {
	display: flex;
	align-items: center;
	gap: 10px;
}

/* changelog button: a small, light outline chip after the sign-in button */
.topbar-version {
	position: relative;
	font-family: var(--font-mono);
	font-size: 12px;
	font-weight: 500;
	color: var(--muted-2);
	background: transparent;
	border: 1px solid var(--hairline);
	border-radius: 4px;
	padding: 7px 9px;
	cursor: pointer;
	transition: border-color 140ms ease, color 140ms ease;
}

.topbar-version:hover {
	color: var(--ink);
	border-color: var(--muted-2);
}

/* unread changelog: a red dot at the top-right corner, and a periodic shake */
.topbar-version-dot {
	position: absolute;
	top: -4px;
	right: -4px;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: var(--wrong);
	border: 1.5px solid var(--paper);
}

.topbar-version.is-unread {
	color: var(--ink);
	border-color: var(--muted-2);
	/* the keyframes are mostly still, with a brief shake near the end, so it
	   "shakes every few seconds" rather than continuously */
	animation: topbar-version-shake 4s ease-in-out infinite;
	transform-origin: center;
}

@keyframes topbar-version-shake {
	0%, 88%, 100% { transform: rotate(0); }
	90% { transform: rotate(-9deg); }
	92% { transform: rotate(8deg); }
	94% { transform: rotate(-6deg); }
	96% { transform: rotate(5deg); }
	98% { transform: rotate(-2deg); }
}

@media (prefers-reduced-motion: reduce) {
	.topbar-version.is-unread { animation: none; }
}

.topbar-signin {
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 600;
	color: var(--paper);
	background: var(--ink);
	border: none;
	border-radius: 4px;
	padding: 8px 16px;
	cursor: pointer;
	transition: background 140ms ease;
}

.topbar-signin:hover {
	background: #34302a;
}

/* desktop shows the "Sign in" text; the icon only appears on mobile */
.topbar-signin-icon {
	display: none;
	width: 15px;
	height: 15px;
	fill: currentColor;
}

.topbar-account-wrap {
	position: relative;
	display: flex;
	align-items: center;
}

.topbar-account {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 12px 4px 5px;
	text-decoration: none;
	color: var(--ink);
	background: #f4eee2;
	border: 1px solid var(--hairline);
	border-radius: 4px;
	font: inherit;
	cursor: pointer;
	transition: border-color 140ms ease, background 140ms ease;
}

.topbar-account:hover {
	border-color: var(--muted-2);
	background: #efe7d7;
}

.topbar-menu {
	position: absolute;
	top: calc(100% + 8px);
	right: 0;
	min-width: 160px;
	background: var(--paper);
	border: 1px solid var(--hairline);
	border-radius: 10px;
	box-shadow: 0 14px 40px rgba(40, 34, 24, 0.16);
	padding: 6px;
	z-index: 50;
	display: flex;
	flex-direction: column;
}

.topbar-menu-name {
	font-family: var(--font-body);
	font-size: 13px;
	font-weight: 500;
	color: var(--ink);
	padding: 8px 10px 6px;
	border-bottom: 1px solid var(--hairline);
	margin-bottom: 4px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.topbar-menu-item {
	display: block;
	width: 100%;
	text-align: left;
	background: none;
	border: none;
	border-radius: 7px;
	padding: 8px 10px;
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--ink);
	cursor: pointer;
}

.topbar-menu-item:hover {
	background: #f1ebdd;
	color: var(--accent);
}

.topbar-avatar {
	width: 22px;
	height: 22px;
	border-radius: 50%;
	background-color: #ece5d7;
	border: 1px solid var(--hairline);
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	overflow: hidden;
}

.topbar-avatar-img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.topbar-initial {
	font-family: var(--font-display);
	font-weight: 600;
	font-size: 11px;
	color: #fff;
}

.topbar-name {
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 500;
	color: var(--ink);
}

/* phones: keep all three destinations reachable. The wordmark + Sign in /
   account stay on the first row; the nav wraps to its own centred row beneath
   so it never crowds the bar (Archive / Leaderboard are otherwise unreachable
   on mobile). */
@media (max-width: 599px) {

	.topbar {
		flex-wrap: wrap;
		row-gap: 10px;
		padding: 12px 16px;
	}

	.topbar-nav {
		order: 3;
		flex-basis: 100%;
		/* cancel the topbar's 16px side padding so the divider runs edge to edge,
		   then re-pad the links so they don't touch the screen sides */
		margin: 0 -16px;
		justify-content: center;
		gap: 22px;
		padding: 10px 16px 0;
		border-top: 1px solid var(--hairline);
	}

	.topbar-name {
		display: none;
	}

	/* name hidden -> the account chip, the changelog button and the (guest)
	   sign-in button are all square 32x32 icon buttons, sized to match exactly */
	.topbar-account,
	.topbar-version,
	.topbar-signin {
		box-sizing: border-box;
		width: 32px;
		height: 32px;
		padding: 0;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	/* sign-in collapses to the user icon (no text) on mobile */
	.topbar-signin-text {
		display: none;
	}

	.topbar-signin-icon {
		display: block;
	}

}

</style>
